import React, { useState } from 'react';
import { FiBarChart2, FiPieChart, FiTrendingUp } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReactECharts from 'echarts-for-react';

const MetricCard = ({ title, value, subvalue, icon, color, chartData }) => {
  const [chartType, setChartType] = useState('none');

  const getColorClasses = (color) => {
    // Simplified color scheme with darker backgrounds for minimalist style
    const colors = {
      'green': {
        bg: 'bg-green-50 dark:bg-green-900/30',
        text: 'text-green-700 dark:text-green-400',
        border: 'border-green-200 dark:border-green-800/50',
        chartColors: ['#15803d', '#22c55e', '#4ade80', '#86efac']
      },
      'amber': {
        bg: 'bg-amber-50 dark:bg-amber-900/30',
        text: 'text-amber-700 dark:text-amber-400',
        border: 'border-amber-200 dark:border-amber-800/50',
        chartColors: ['#b45309', '#d97706', '#f59e0b', '#fbbf24']
      },
      'red': {
        bg: 'bg-red-50 dark:bg-red-900/30',
        text: 'text-red-700 dark:text-red-400',
        border: 'border-red-200 dark:border-red-800/50',
        chartColors: ['#b91c1c', '#dc2626', '#ef4444', '#f87171']
      },
      // Keep blue as a neutral option
      'blue': {
        bg: 'bg-blue-50 dark:bg-blue-900/30',
        text: 'text-blue-700 dark:text-blue-400',
        border: 'border-blue-200 dark:border-blue-800/50',
        chartColors: ['#1d4ed8', '#3b82f6', '#60a5fa', '#93c5fd']
      }
    };
    return colors[color] || colors.blue;
  };

  const colorClasses = getColorClasses(color);

  // Chart options based on type
  const getChartOption = () => {
    const isDarkMode = document.documentElement.classList.contains('dark');
    const textColor = isDarkMode ? '#e5e7eb' : '#374151';
    const axisLineColor = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';

    // Default styling
    const defaultOptions = {
      textStyle: {
        color: textColor,
        fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif'
      },
      tooltip: {
        trigger: 'item'
      },
      grid: {
        top: 10,
        right: 20,
        bottom: 80, // Increased bottom space for labels
        left: 40, // Increased left space for y-axis labels
        containLabel: true
      },
      color: colorClasses.chartColors
    };

    // If chartData is not available, show a placeholder
    if (!chartData || chartData.length === 0) {
      return {
        ...defaultOptions,
        title: {
          text: 'No data available',
          textStyle: {
            color: textColor,
            fontSize: 12
          },
          left: 'center',
          top: 'center'
        }
      };
    }

    switch (chartType) {
      case 'pie':
        return {
          ...defaultOptions,
          legend: {
            type: 'scroll',
            orient: 'vertical',
            right: 10,
            top: 'center',
            itemWidth: 10,
            itemHeight: 10,
            textStyle: {
              color: textColor,
              fontSize: 11
            },
            formatter: function(name) {
              const item = chartData.find(d => d.name === name);
              if (item) {
                return `${name}: ${item.value}`;
              }
              return name;
            }
          },
          tooltip: {
            trigger: 'item',
            formatter: '{b}: {c} ({d}%)',
            backgroundColor: isDarkMode ? 'rgba(48, 48, 48, 0.9)' : 'rgba(255, 255, 255, 0.9)',
            borderColor: isDarkMode ? '#555' : '#ddd',
            textStyle: {
              color: textColor
            }
          },
          series: [{
            type: 'pie',
            radius: '50%',
            center: ['30%', '50%'], // Move pie to the left to make room for legend
            avoidLabelOverlap: true,
            itemStyle: {
              borderColor: isDarkMode ? '#1f2937' : '#ffffff',
              borderWidth: 2
            },
            label: {
              show: false
            },
            labelLine: {
              show: false
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0,0,0,0.5)'
              },
              label: {
                show: false
              }
            },
            data: chartData.map(item => ({
              name: item.name,
              value: item.value
            }))
          }]
        };
      case 'bar':
        // Get the maximum value for y-axis scaling
        const maxValue = Math.max(...chartData.map(item => item.value));
        // Add 20% padding to the top of the chart
        const yAxisMax = Math.ceil(maxValue * 1.2);
        
        return {
          ...defaultOptions,
          xAxis: {
            type: 'category',
            data: chartData.map(item => item.name),
            axisLine: {
              lineStyle: {
                color: axisLineColor
              }
            },
            axisLabel: {
              fontSize: 10,
              rotate: 45,
              interval: 0,
              color: textColor,
              overflow: 'break', // Break text if it's too long
              width: 80, // Maximum width for labels
              ellipsis: '...',
              hideOverlap: false // Show all labels
            }
          },
          yAxis: {
            type: 'value',
            max: yAxisMax, // Set the maximum value with 20% padding
            axisLine: {
              lineStyle: {
                color: axisLineColor
              }
            },
            splitLine: {
              lineStyle: {
                color: axisLineColor,
                opacity: 0.5
              }
            }
          },
          grid: {
            top: 10,
            right: 20,
            bottom: 80, // Increased bottom space for rotated labels
            left: 40, // Increased left space for y-axis labels
            containLabel: true
          },
          series: [{
            data: chartData.map(item => item.value),
            type: 'bar',
            barWidth: '60%',
            itemStyle: {
              borderRadius: [4, 4, 0, 0]
            },
            label: {
              show: true,
              position: 'top',
              fontSize: 10,
              color: textColor
            }
          }]
        };
      case 'line':
        // Similar y-axis padding for line chart
        const lineMaxValue = Math.max(...chartData.map(item => item.value));
        const lineYAxisMax = Math.ceil(lineMaxValue * 1.2);
        
        return {
          ...defaultOptions,
          xAxis: {
            type: 'category',
            data: chartData.map(item => item.name),
            axisLine: {
              lineStyle: {
                color: axisLineColor
              }
            },
            axisLabel: {
              fontSize: 10,
              rotate: 45,
              interval: 0,
              color: textColor
            }
          },
          yAxis: {
            type: 'value',
            max: lineYAxisMax, // Set the maximum value with 20% padding
            axisLine: {
              lineStyle: {
                color: axisLineColor
              }
            },
            splitLine: {
              lineStyle: {
                color: axisLineColor,
                opacity: 0.5
              }
            }
          },
          series: [{
            data: chartData.map(item => item.value),
            type: 'line',
            smooth: true,
            symbolSize: 8,
            label: {
              show: true,
              position: 'top',
              fontSize: 10,
              color: textColor
            }
          }]
        };
      default:
        return defaultOptions;
    }
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-4 transition-all duration-300 ${chartType !== 'none' ? (chartType === 'bar' ? 'h-[450px]' : 'h-[450px]') : ''} flex flex-col`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
        <div className="min-h-[40px]"> {/* Reduced minimum height for title area */}
          <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          {subvalue && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{subvalue}</p>
          )}
        </div>
        <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center border mt-2 md:mt-0 ${colorClasses.bg} ${colorClasses.text} ${colorClasses.border}`}>
          <SafeIcon icon={icon} className="w-5 h-5 md:w-6 md:h-6" />
        </div>
      </div>

      {/* Chart display area with increased height and padding */}
      {chartType !== 'none' && chartData && (
        <div className="h-[320px] mt-2 flex-grow px-2 py-4"> {/* Increased height for better visualization */}
          <ReactECharts 
            option={getChartOption()} 
            style={{height: '100%', width: '100%'}}
            opts={{renderer: 'svg'}} // Use SVG renderer for better text rendering
          />
        </div>
      )}

      {/* Chart type selection - Centered at bottom with fixed positioning */}
      <div className="flex justify-center items-center mt-auto pt-2 border-t border-gray-100 dark:border-gray-700/50">
        <div className="flex space-x-2">
          <button
            onClick={() => setChartType(chartType === 'line' ? 'none' : 'line')}
            className={`p-2 rounded-md transition-colors ${chartType === 'line' ? `${colorClasses.bg} ${colorClasses.text}` : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="Line Chart"
          >
            <SafeIcon icon={FiTrendingUp} className="w-4 h-4" />
          </button>
          <button
            onClick={() => setChartType(chartType === 'bar' ? 'none' : 'bar')}
            className={`p-2 rounded-md transition-colors ${chartType === 'bar' ? `${colorClasses.bg} ${colorClasses.text}` : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="Bar Chart"
          >
            <SafeIcon icon={FiBarChart2} className="w-4 h-4" />
          </button>
          <button
            onClick={() => setChartType(chartType === 'pie' ? 'none' : 'pie')}
            className={`p-2 rounded-md transition-colors ${chartType === 'pie' ? `${colorClasses.bg} ${colorClasses.text}` : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="Pie Chart"
          >
            <SafeIcon icon={FiPieChart} className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MetricCard;